import React, {
  useState,
  useEffect,
  useCallback,
  ReactElement,
  ReactNode,
} from "react";
import { Modal } from "../Modal";
import { Button } from "../../Button";

export interface Props {
  modalId: string;
  modalDataTestId?: string;
  modalTitle: string;
  modalButtons: React.ComponentProps<typeof Button>[];
  modalChildren?: ReactNode;
  modalAlignButtons?: string;
}

type TriggerFunction = (...args: any[]) => void;

export const useModal = (props: Props): [TriggerFunction, ReactElement] => {
  const [isOpen, setIsOpen] = useState(false);
  const [args, setArgs] = useState({});

  const trigger: TriggerFunction = useCallback((args: any) => {
    setArgs(args);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [trigger, isOpen]);

  const modalActionButtons: React.ComponentProps<typeof Button>[] = [];

  props.modalButtons.forEach((modalButton) => {
    const modalAction = modalButton.onClick;
    const modalButtonOnClick = () => {
      modalAction?.(args);
      setIsOpen(false);
    };
    modalActionButtons.push({ ...modalButton, onClick: modalButtonOnClick });
  });

  return [
    trigger,
    <Modal
      key={props.modalId}
      id={props.modalId}
      dataTestId={props.modalDataTestId}
      isOpen={isOpen}
      title={props.modalTitle}
      modalButtons={modalActionButtons}
      alignButtons={props.modalAlignButtons}
    >
      {props.modalChildren}
    </Modal>,
  ];
};
